<template>
  <v-container class="help-center-container">
    <!-- Estado 1: Centro de Ayuda (inicio) -->
    <div v-if="currentState === 'hub'" class="help-hub">
      <div class="help-header mb-6">
        <h1 class="display-1 font-weight-bold mb-2">Centro de Ayuda</h1>
        <p class="subheading grey--text mb-0">Encuentra respuestas rápidas o crea un ticket de soporte</p>
        <v-btn
          v-if="hasTickets && isAdmin"
          flat
          dark
          small
          class="mt-3 text-none font-weight-bold"
          @click="goToMyTickets"
        >
          <v-icon left small>confirmation_number</v-icon>
          Ver Mis Tickets
        </v-btn>
      </div>

      <!-- Tabs: Manuales y FAQ -->
      <v-layout row wrap>
        <v-flex xs12>
          <v-tabs v-model="hubTab" color="primary" slider-color="primary" class="help-tabs">
            <v-tab href="#manuales" class="font-weight-bold tab-label">
              <v-icon left small>school</v-icon>
              Manuales
            </v-tab>
            <v-tab href="#faq" class="font-weight-bold tab-label">
              <v-icon left small>help</v-icon>
              Preguntas Frecuentes
            </v-tab>

            <!-- Tab: Manuales -->
            <v-tab-item value="manuales" id="manuales" class="tab-content">
              <v-layout row wrap class="mb-6">
                <v-flex xs12>
                  <v-text-field
                    v-model="searchQuery"
                    placeholder="Buscar en manuales..."
                    prepend-icon="search"
                    style="max-width: 100%;"
                    @input="filterManuals"
                    outlined
                    rounded
                    dense
                  ></v-text-field>
                </v-flex>
              </v-layout>

              <v-layout row wrap v-if="filteredManuals.length > 0">
                <v-flex xs12>
                  <h3 class="subheading font-weight-bold mb-4">Manuales Disponibles</h3>
                  <v-list two-line class="manual-list-container">
                    <v-list-tile
                      v-for="(manual, idx) in filteredManuals"
                      :key="idx"
                      @click="startReadingManual(manual)"
                      class="manual-list-item"
                    >
                      <v-list-tile-avatar>
                        <v-icon :color="manual.color">{{ manual.icon }}</v-icon>
                      </v-list-tile-avatar>
                      <v-list-tile-content>
                        <v-list-tile-title class="font-weight-bold">{{ manual.title }}</v-list-tile-title>
                        <v-list-tile-sub-title>{{ manual.description }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>
                  </v-list>
                </v-flex>
              </v-layout>

              <v-layout row wrap v-else class="py-6">
                <v-flex xs12 class="text-center">
                  <v-icon size="48" color="grey lighten-2" class="mb-3">find_in_page</v-icon>
                  <p class="grey--text">No se encontraron manuales que coincidan con tu búsqueda.</p>
                </v-flex>
              </v-layout>
            </v-tab-item>

            <!-- Tab: FAQ -->
            <v-tab-item value="faq" id="faq" class="tab-content">
              <v-layout row wrap class="mb-6">
                <v-flex xs12>
                  <v-text-field
                    v-model="faqSearchQuery"
                    placeholder="Buscar preguntas frecuentes..."
                    prepend-icon="search"
                    style="max-width: 100%;"
                    outlined
                    rounded
                    dense
                  ></v-text-field>
                </v-flex>
              </v-layout>

              <v-layout row wrap v-if="filteredFaqs.length > 0">
                <v-flex xs12>
                  <h3 class="subheading font-weight-bold mb-4">Preguntas Frecuentes</h3>
                  <v-list two-line class="manual-list-container faq-list-container">
                    <v-list-group
                      v-for="(faq, idx) in filteredFaqs"
                      :key="idx"
                      no-action
                      class="manual-list-item faq-list-item"
                    >
                      <v-list-tile slot="activator">
                        <v-list-tile-avatar>
                          <v-icon color="primary">{{ faq.icon }}</v-icon>
                        </v-list-tile-avatar>
                        <v-list-tile-content>
                          <v-list-tile-title class="font-weight-bold">{{ faq.question }}</v-list-tile-title>
                        </v-list-tile-content>
                      </v-list-tile>

                      <v-list-tile class="faq-answer-tile">
                        <v-list-tile-content>
                          <p class="body-1 faq-answer-text mb-0">{{ faq.answer }}</p>
                          <div v-if="faq.tips" class="faq-tips mt-3">
                            <strong class="faq-tips-label">
                              <v-icon small color="#1976d2" class="mr-1">lightbulb_outline</v-icon>
                              Tips:
                            </strong>
                            <p class="caption mb-0 mt-1">{{ faq.tips }}</p>
                          </div>
                        </v-list-tile-content>
                      </v-list-tile>
                    </v-list-group>
                  </v-list>
                </v-flex>
              </v-layout>

              <v-layout row wrap v-else class="py-6">
                <v-flex xs12 class="text-center">
                  <v-icon size="48" color="grey lighten-2" class="mb-3">help_outline</v-icon>
                  <p class="grey--text">No se encontraron preguntas que coincidan con tu búsqueda.</p>
                </v-flex>
              </v-layout>
            </v-tab-item>
          </v-tabs>
        </v-flex>
      </v-layout>
    </div>

    <!-- Estado 2: Leyendo Manual -->
    <div v-if="currentState === 'reading-manual'" class="reading-state">
      <!-- Toolbar -->
      <v-toolbar flat class="mb-4" style="background: transparent;">
        <v-btn icon @click="currentState = 'hub'">
          <v-icon>arrow_back</v-icon>
        </v-btn>
        <v-toolbar-title class="font-weight-bold">{{ readingManual.title }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          v-if="numPages > 0"
          icon
          @click="printPdf"
          title="Imprimir"
        >
          <v-icon>print</v-icon>
        </v-btn>
        <v-btn
          v-if="readingManual.link !== '#'"
          icon
          :href="readingManual.link"
          target="_blank"
          title="Abrir en nueva pestaña"
        >
          <v-icon>open_in_new</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- PDF Viewer -->
      <div class="pdf-viewer-container">
        <div v-if="loadingPdf" class="loading-state">
          <v-progress-circular indeterminate color="primary" size="64" class="mb-4"></v-progress-circular>
          <span class="subheading grey--text">Cargando documento...</span>
        </div>

        <div v-else-if="!readingManual.link || readingManual.link === '#'" class="not-available-state">
          <v-icon size="88" color="grey lighten-2" class="mb-5">construction</v-icon>
          <h3 class="headline font-weight-bold grey--text text--darken-2 text-center">Documento en Construcción</h3>
          <p class="body-1 grey--text text--darken-1 mt-3 text-center">Este manual se encuentra en proceso de elaboración.</p>
        </div>

        <div v-else class="pdf-content">
          <pdf ref="myPdfComponent" style="display: none;" :src="pdfSrc"></pdf>
          <div v-for="i in numPages" :key="i" class="pdf-page-wrapper">
            <pdf :src="pdfSrc" :page="i" style="width: 100%"></pdf>
          </div>
        </div>
      </div>

      <!-- Resolution Panel -->
      <v-card class="mt-6 elevation-2" style="background: #f5f5f5; border-radius: 12px;">
        <v-card-text class="pa-6">
          <h3 class="subheading font-weight-bold mb-4">¿Se resolvió tu problema?</h3>
          <div class="resolution-buttons">
            <v-btn
              color="success"
              large
              outline
              @click="resolvedProblem"
              class="font-weight-bold text-none mr-3"
              style="border-radius: 8px; min-width: 120px;"
            >
              <v-icon left>check_circle</v-icon>
              Sí, se resolvió
            </v-btn>
            <v-btn
              color="error"
              large
              outline
              @click="goToCreateTicket"
              class="font-weight-bold text-none"
              style="border-radius: 8px; min-width: 120px;"
            >
              <v-icon left>cancel</v-icon>
              No, crear ticket
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <!-- Estado 3: Crear Ticket -->
    <div v-if="currentState === 'create-ticket'" class="create-ticket-state">
      <v-toolbar flat class="mb-4" style="background: transparent;">
        <v-btn icon @click="currentState = 'hub'">
          <v-icon>arrow_back</v-icon>
        </v-btn>
        <v-toolbar-title class="font-weight-bold">Crear Ticket de Soporte</v-toolbar-title>
      </v-toolbar>

      <v-layout row wrap>
        <!-- Main form -->
        <v-flex xs12 md8>
          <v-card class="elevation-1">
            <v-card-text class="pa-6">
              <v-form ref="ticketForm">
                <v-text-field
                  v-model="ticketForm.title"
                  label="Título del problema"
                  placeholder="Ej: No puedo conectarme al WiFi"
                  prepend-icon="edit"
                  :error-messages="errores.title"
                  counter="100"
                  maxlength="100"
                  class="mb-2"
                  @input="updateRelatedManuals"
                ></v-text-field>

                <v-textarea
                  v-model="ticketForm.description"
                  label="Descripción detallada"
                  placeholder="Describe lo que intentaste, qué sucedió y cualquier mensaje de error..."
                  prepend-icon="notes"
                  :error-messages="errores.description"
                  rows="8"
                  counter="2000"
                  maxlength="2000"
                  class="mb-2"
                  @input="updateRelatedManuals"
                ></v-textarea>

                <v-select
                  v-model="ticketForm.area_id"
                  label="Área"
                  :items="areas"
                  item-text="name"
                  item-value="id"
                  prepend-icon="folder"
                  :error-messages="errores.area_id"
                  :loading="loadingAreas"
                  class="mb-2"
                ></v-select>

                <v-text-field
                  v-model="ticketForm.client_name"
                  label="Nombre del cliente"
                  prepend-icon="person"
                  :error-messages="errores.client_name"
                  readonly
                  class="mb-2"
                ></v-text-field>

                <v-text-field
                  v-model="ticketForm.client_email"
                  type="email"
                  label="Email"
                  prepend-icon="email"
                  :error-messages="errores.client_email"
                  readonly
                  class="mb-2"
                ></v-text-field>

                <v-text-field
                  v-model="ticketForm.client_phone"
                  label="Teléfono"
                  prepend-icon="phone"
                  :error-messages="errores.client_phone"
                  readonly
                  class="mb-6"
                ></v-text-field>

                <v-alert :value="!!errorAlert" type="error" outline class="mb-4">
                  {{ errorAlert }}
                </v-alert>

                <v-btn
                  color="primary"
                  large
                  block
                  :loading="submittingTicket"
                  class="font-weight-bold text-none"
                  style="border-radius: 8px; height: 48px;"
                  @click="submitTicket"
                >
                  <v-icon left>send</v-icon>
                  Enviar Ticket
                </v-btn>
              </v-form>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- Related manuals sidebar -->
        <v-flex xs12 md4>
          <v-card class="elevation-1" style="background: #f0f8ff; border-left: 4px solid #2196F3; border-radius: 8px;">
            <v-card-text class="pa-6">
              <h4 class="subheading font-weight-bold mb-3 related-manuals-title">
                <v-icon small color="#2196F3">lightbulb_outline</v-icon>
                Posibles soluciones
              </h4>

              <div v-if="relatedManuals.length === 0" class="text-center py-6">
                <p class="caption grey--text">Escribe tu problema para ver manuales relacionados...</p>
              </div>

              <div v-else>
                <v-list dense>
                  <v-list-tile
                    v-for="(manual, idx) in relatedManuals"
                    :key="idx"
                    @click="startReadingManual(manual)"
                    class="related-manual-item"
                  >
                    <v-list-tile-avatar style="min-width: 32px;">
                      <v-icon small :color="manual.color">{{ manual.icon }}</v-icon>
                    </v-list-tile-avatar>
                    <v-list-tile-content>
                      <v-list-tile-title class="caption font-weight-bold">
                        {{ manual.title }}
                      </v-list-tile-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </v-list>

                <v-divider class="my-3"></v-divider>
                <p class="caption grey--text">Haz clic en un manual para revisar antes de enviar el ticket</p>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </div>

    <!-- Estado 4: Mis Tickets -->
    <div v-if="currentState === 'my-tickets'" class="my-tickets-state">
      <v-toolbar flat class="mb-4" style="background: transparent;">
        <v-btn icon @click="currentState = 'hub'">
          <v-icon>arrow_back</v-icon>
        </v-btn>
        <v-toolbar-title class="font-weight-bold">Mis Tickets</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="loadTickets" :loading="loadingTickets" title="Actualizar">
          <v-icon>refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <v-layout row wrap class="mb-4">
        <v-flex xs12>
          <v-btn
            color="primary"
            @click="goToCreateTicket"
            class="font-weight-bold text-none"
            style="border-radius: 8px;"
          >
            <v-icon left>add_circle</v-icon>
            Nuevo Ticket
          </v-btn>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-if="tickets.length > 0">
        <v-flex xs12>
          <v-list two-line>
            <v-list-tile
              v-for="ticket in tickets"
              :key="ticket.id"
              @click="openTicketDetails(ticket)"
              class="ticket-list-item"
            >
              <v-list-tile-content>
                <v-list-tile-title class="font-weight-bold">{{ ticket.folio }} — {{ ticket.title }}</v-list-tile-title>
                <v-list-tile-sub-title>{{ formatDate(ticket.created_date) }}</v-list-tile-sub-title>
              </v-list-tile-content>
              <v-list-tile-action>
                <v-chip :color="getStatusColor(ticket.status)" text-color="white" small>
                  {{ ticket.status }}
                </v-chip>
              </v-list-tile-action>
            </v-list-tile>
          </v-list>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-else>
        <v-flex xs12 class="text-xs-center py-6">
          <v-progress-circular v-if="loadingTickets" indeterminate color="primary" size="48"></v-progress-circular>
          <div v-else>
            <v-icon size="64" color="grey lighten-1" class="mb-3">inbox</v-icon>
            <p class="grey--text">No tienes tickets registrados.</p>
          </div>
        </v-flex>
      </v-layout>
    </div>

    <!-- Ticket Details Dialog -->
    <v-dialog v-model="ticketDetailsDialog" max-width="500">
      <v-card v-if="selectedTicket" class="elevation-2">
        <v-toolbar flat dark color="primary">
          <v-toolbar-title class="font-weight-bold">Ticket {{ selectedTicket.folio || ('#' + selectedTicket.id) }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="ticketDetailsDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-6">
          <div class="mb-4">
            <small class="grey--text">Título</small>
            <p class="body-1 font-weight-bold mb-0">{{ selectedTicket.title }}</p>
          </div>

          <div class="mb-4">
            <small class="grey--text">Descripción</small>
            <p class="body-1 mb-0">{{ selectedTicket.description || '—' }}</p>
          </div>

          <v-layout row wrap class="mb-4">
            <v-flex xs6>
              <small class="grey--text">Estado</small>
              <div class="mt-1">
                <v-chip :color="getStatusColor(selectedTicket.status)" text-color="white" small>
                  {{ selectedTicket.status }}
                </v-chip>
              </div>
            </v-flex>
            <v-flex xs6>
              <small class="grey--text">Área</small>
              <p class="body-1 mb-0 mt-1">{{ selectedTicket.area || '—' }}</p>
            </v-flex>
          </v-layout>

          <v-layout row wrap class="mb-4" v-if="selectedTicket.responsable">
            <v-flex xs12>
              <small class="grey--text">Responsable</small>
              <p class="body-1 mb-0">{{ selectedTicket.responsable }}</p>
            </v-flex>
          </v-layout>

          <div class="mb-4" v-if="selectedTicket.tags && selectedTicket.tags.length > 0">
            <small class="grey--text">Etiquetas</small>
            <div class="mt-1">
              <v-chip
                v-for="tag in selectedTicket.tags"
                :key="tag.id"
                small
                color="info"
                text-color="white"
                class="mr-2"
              >
                {{ tag.name }}
              </v-chip>
            </div>
          </div>

          <div class="mb-0">
            <small class="grey--text">Fecha de creación</small>
            <p class="body-1 mb-0">{{ formatDate(selectedTicket.created_date) }}</p>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" flat @click="ticketDetailsDialog = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Dialog -->
    <v-dialog v-model="successDialog" max-width="400">
      <v-card class="elevation-2">
        <v-card-text class="text-center pa-6">
          <v-icon size="64" color="success" class="mb-4">check_circle</v-icon>
          <h3 class="headline font-weight-bold mb-2">¡Problema Resuelto!</h3>
          <p class="body-2 grey--text">Nos alegra haber podido ayudarte. Si tienes más preguntas, estamos aquí para ti.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" flat @click="successDialog = false; currentState = 'hub'">
            Volver al Centro de Ayuda
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Ticket Submitted Dialog -->
    <v-dialog v-model="ticketSubmittedDialog" max-width="400">
      <v-card class="elevation-2">
        <v-card-text class="text-center pa-6">
          <v-icon size="64" color="primary" class="mb-4">confirmation_number</v-icon>
          <h3 class="headline font-weight-bold mb-2">¡Ticket Creado!</h3>
          <p class="body-2 grey--text mb-4">Tu ticket ha sido enviado exitosamente. Guarda este folio para dar seguimiento.</p>
          <p class="caption" style="font-family: monospace; background: #f5f5f5; padding: 8px; border-radius: 4px;">
            Folio: {{ lastTicketFolio }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" flat @click="resetTicketForm(); ticketSubmittedDialog = false">
            Crear otro ticket
          </v-btn>
          <v-btn color="primary" @click="ticketSubmittedDialog = false; goToMyTickets()">
            Ver mis tickets
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Floating Help Bubble -->
    <div v-if="currentState === 'hub' && isAdmin" class="floating-help-bubble" @click="showTicketPanel = true">
      <v-icon size="32">help_outline</v-icon>
    </div>

    <!-- Sliding Panel (Bottom Up) -->
    <transition name="slide-up">
      <div v-if="showTicketPanel" class="ticket-panel-overlay" @click="showTicketPanel = false">
        <v-card class="ticket-panel" @click.stop>
          <!-- Panel Header -->
          <div class="ticket-panel-header">
            <h3 class="subheading font-weight-bold">Crear Ticket de Soporte</h3>
            <v-btn icon small @click="showTicketPanel = false">
              <v-icon>close</v-icon>
            </v-btn>
          </div>

          <!-- Panel Content -->
          <div class="ticket-panel-content">
            <v-form ref="quickTicketForm">
              <v-text-field
                v-model="quickTicketForm.title"
                label="¿Cuál es tu problema?"
                placeholder="Describe brevemente..."
                prepend-icon="edit"
                :error-messages="errores.title"
                counter="100"
                maxlength="100"
                class="mb-3"
              ></v-text-field>

              <v-textarea
                v-model="quickTicketForm.description"
                label="Detalles adicionales (opcional)"
                placeholder="Proporciona más información..."
                prepend-icon="notes"
                rows="4"
                counter="500"
                maxlength="500"
                class="mb-3"
              ></v-textarea>

              <v-select
                v-model="quickTicketForm.area_id"
                label="Área"
                :items="areas"
                item-text="name"
                item-value="id"
                prepend-icon="folder"
                :loading="loadingAreas"
                class="mb-4"
              ></v-select>

              <v-alert :value="!!errorAlert" type="error" outline class="mb-4">
                {{ errorAlert }}
              </v-alert>

              <v-btn
                color="primary"
                block
                :loading="submittingTicket"
                class="font-weight-bold text-none"
                style="border-radius: 8px; height: 44px;"
                @click="submitQuickTicket"
              >
                <v-icon left>send</v-icon>
                Enviar Ticket
              </v-btn>
            </v-form>
          </div>
        </v-card>
      </div>
    </transition>
  </v-container>
</template>

<script>
    import pdf from 'vue-pdf'
    import { mapGetters } from 'vuex'
    import { ticketsService } from '@/services/ticketsService'

    export default {
        name: "ayuda_admin",
        components: { pdf },
        data () {
            return {
                currentState: 'hub',
                hubTab: 'manuales',
                showTicketPanel: false,
                searchQuery: '',
                faqSearchQuery: '',
                filteredManuals: [],
                readingManual: null,
                pdfSrc: null,
                numPages: 0,
                loadingPdf: false,
                ticketForm: {
                    title: '',
                    description: '',
                    area_id: null,
                    client_name: '',
                    client_email: '',
                    client_phone: ''
                },
                quickTicketForm: {
                    title: '',
                    description: '',
                    area_id: null
                },
                relatedManuals: [],
                areas: [],
                tickets: [],
                selectedTicket: null,
                loadingAreas: false,
                loadingTickets: false,
                successDialog: false,
                ticketSubmittedDialog: false,
                ticketDetailsDialog: false,
                submittingTicket: false,
                lastTicketFolio: null,
                errores: {},
                errorAlert: '',
                faqs: [
                    {
                        icon: 'person_add',
                        question: '¿Cómo creo una invitación para un visitante?',
                        answer: 'Dirígete a la sección de Invitaciones, completa el formulario con los datos del visitante, especifica la fecha y hora de acceso, y envía la invitación. El visitante recibirá un código QR para escanear en la entrada.',
                        tips: 'Prepara los datos del visitante antes de crear la invitación. Asegúrate de incluir un número de teléfono válido para que reciba notificaciones.'
                    },
                    {
                        icon: 'lock_reset',
                        question: '¿Qué debo hacer si olvido mi contraseña?',
                        answer: 'En la pantalla de inicio de sesión, haz clic en "¿Olvidaste tu contraseña?". Ingresa tu correo electrónico y recibirás un enlace para restaurar tu contraseña.',
                        tips: 'Revisa tu bandeja de spam si no recibes el correo en 5 minutos. El enlace es válido por 24 horas.'
                    },
                    {
                        icon: 'local_parking',
                        question: '¿Cómo reservo un espacio de estacionamiento?',
                        answer: 'En la sección Parking, selecciona el área disponible, elige la fecha y hora de tu reserva, y confirma. Recibirás una confirmación con tu código de reserva.',
                        tips: 'Las reservas se pueden hacer con hasta 7 días de anticipación. Puedes modificar o cancelar tu reserva hasta 2 horas antes.'
                    },
                    {
                        icon: 'history',
                        question: '¿Cómo veo el historial de mis accesos?',
                        answer: 'Dirígete a tu perfil y selecciona "Historial de Accesos". Allí podrás ver todos tus movimientos registrados con fecha, hora y ubicación.',
                        tips: 'El historial se guarda durante 90 días. Puedes filtrar por fecha o tipo de acceso para encontrar registros específicos.'
                    },
                    {
                        icon: 'qr_code_scanner',
                        question: '¿Qué hago si no puedo escanear un código QR?',
                        answer: 'Asegúrate de que tu cámara está habilitada en los permisos de la aplicación. Si persiste el problema, reinicia la aplicación o contacta a soporte mediante un ticket.',
                        tips: 'Limpia la lente de tu cámara y asegúrate de que haya buena iluminación. Intenta acercarte más al código QR.'
                    },
                    {
                        icon: 'schedule',
                        question: '¿Cuál es el horario de atención del equipo de soporte?',
                        answer: 'Nuestro equipo de soporte atiende de lunes a viernes de 8:00 AM a 6:00 PM. Los tickets creados fuera de horario serán atendidos al siguiente día hábil.',
                        tips: 'Para problemas urgentes, usa la sección de contacto de emergencia en tu perfil.'
                    },
                    {
                        icon: 'edit',
                        question: '¿Cómo cambio mis datos de contacto?',
                        answer: 'En la sección de Perfil, edita tus datos de contacto (teléfono, correo), guarda los cambios y recibirás una confirmación de actualización.',
                        tips: 'Es importante mantener tu información actualizada para recibir notificaciones importantes y alertas de seguridad.'
                    },
                    {
                        icon: 'support_agent',
                        question: '¿Qué debo incluir en un ticket de soporte?',
                        answer: 'Incluye un título descriptivo, una descripción detallada del problema, qué intentaste, qué sucedió y cualquier mensaje de error que recibiste. Esto ayudará a nuestro equipo a resolver tu problema más rápidamente.',
                        tips: 'Adjunta capturas de pantalla si es posible. Proporciona información sobre el dispositivo y navegador que utilizas.'
                    }
                ]
            }
        },
        computed: {
            ...mapGetters(['getProfile']),
            isAdmin() {
                const profile = this.getProfile || {}
                return profile.roll === 5 || profile.is_superuser === true
            },
            hasTickets() {
                return this.tickets.length > 0
            },
            locationName() {
                const profile = this.getProfile || {}
                return profile.is_building ? 'edificio corporativo' : 'parque'
            },
            locationNameCapitalized() {
                const profile = this.getProfile || {}
                return profile.is_building ? 'Edificio Corporativo' : 'Parque Industrial'
            },
            filteredFaqs() {
                if (!this.faqSearchQuery.trim()) {
                    return this.faqs
                }
                const query = this.faqSearchQuery.toLowerCase()
                return this.faqs.filter(faq => {
                    const questionMatch = faq.question.toLowerCase().includes(query)
                    const answerMatch = faq.answer.toLowerCase().includes(query)
                    return questionMatch || answerMatch
                })
            },
            availableManuals() {
                if (!this.getProfile || !this.getProfile.roll) return []
                const roll = this.getProfile.roll
                const isSupervisor = this.getProfile.is_supervisor
                const isSuperuser = this.getProfile.is_superuser
                const isShipmentsAnalyst = this.getProfile.shipments_management
                const manuals = []

                if (roll === 5 || isSuperuser) {
                    manuals.push({
                        title: 'Administrador General',
                        description: 'Uso, configuración y gestión integral de la plataforma.',
                        icon: 'admin_panel_settings',
                        link: 'https://intrare-empresa-test.s3.us-east-2.amazonaws.com/manuales/ManualAdministrador21.pdf',
                        color: 'primary',
                        keywords: ['administración', 'general', 'configuración', 'plataforma', 'gestión']
                    })
                }
                if (roll === 4 || roll === 5 || isSuperuser) {
                    manuals.push({
                        title: `Administrador de ${this.locationNameCapitalized}`,
                        description: `Control, administración y monitoreo de empresas del ${this.locationName}.`,
                        icon: 'location_city',
                        link: 'https://intrare-empresa-test.s3.us-east-2.amazonaws.com/manuales/Manual+Usuario+++Administrador+de+Parque+Industrial.pdf',
                        color: 'indigo',
                        keywords: ['parque', 'industrial', 'empresas', 'monitoreo', 'control']
                    })
                }
                if (roll === 5 || isSuperuser || roll === 4) {
                    manuals.push({
                        title: 'Administrador de Parking',
                        description: 'Control y asignación de espacios, accesos y políticas de estacionamiento.',
                        icon: 'local_parking',
                        link: 'https://intrare-empresa.s3.us-east-2.amazonaws.com/Handbook/MANUAL+DE+USUARIO+PARKING.pdf',
                        color: 'blue darken-2',
                        keywords: ['parking', 'estacionamiento', 'espacios', 'accesos', 'reservación']
                    })
                }
                if (isSupervisor || isShipmentsAnalyst || roll === 5 || isSuperuser) {
                    manuals.push({
                        title: 'Supervisor de Embarques',
                        description: 'Gestión en piso, control de tiempos y liberación de cargas.',
                        icon: 'assignment_turned_in',
                        link: 'https://intrare-empresa.s3.us-east-2.amazonaws.com/Handbook/MANUAL+DE+USUARIO+SUPERVISOR+EMBARQUE.pdf',
                        color: 'orange darken-2',
                        keywords: ['embarques', 'supervisor', 'carga', 'tiempos', 'andén']
                    })
                    manuals.push({
                        title: 'Administrador de Embarques',
                        description: 'Configuración logística, asignación de andenes y tiempos de carga.',
                        icon: 'local_shipping',
                        link: 'https://intrare-empresa.s3.us-east-2.amazonaws.com/Handbook/MANUAL+DE+USUARIO+EMBARQUES.pdf',
                        color: 'deep-orange darken-1',
                        keywords: ['embarques', 'logística', 'andenes', 'carga', 'administrador']
                    })
                }
                if (roll === 2 || roll === 3 || roll === 5 || isSuperuser || roll === 4 || roll === 9) {
                    manuals.push({
                        title: 'Vigilante y Caseta',
                        description: 'Escaneo de QR, control de accesos, visitantes y vehículos.',
                        icon: 'security',
                        link: 'https://intrare-empresa.s3.us-east-2.amazonaws.com/Handbook/MANUAL+DE+VIGILANTE.pdf',
                        color: 'blue-grey darken-1',
                        keywords: ['vigilante', 'seguridad', 'qr', 'accesos', 'visitantes']
                    })
                }
                if (roll === 1 || roll === 5 || isSuperuser) {
                    manuals.push({
                        title: 'Usuario Empleado',
                        description: 'Creación de invitaciones, reservación de accesos y perfil personal.',
                        icon: 'badge',
                        link: 'https://intrare-empresa-test.s3.us-east-2.amazonaws.com/manuales/ManualEmpleado21.pdf',
                        color: 'teal darken-1',
                        keywords: ['empleado', 'invitaciones', 'accesos', 'perfil', 'reservación']
                    })
                }
                if (roll === 15 || roll === 11 || roll === 5 || isSuperuser) {
                    manuals.push({
                        title: 'Proveedores y Transportistas',
                        description: 'Pre-registro, agendamiento de visitas y acceso a instalaciones.',
                        icon: 'store',
                        link: 'https://intrare-empresa.s3.us-east-2.amazonaws.com/Handbook/MANUAL+DE+USUARIO+PROVEEDORES.pdf',
                        color: 'brown darken-1',
                        keywords: ['proveedor', 'transportista', 'visitas', 'registro', 'acceso']
                    })
                }

                return manuals
            }
        },
        watch: {
            getProfile: {
                handler(profile) {
                    if (profile && profile.email) {
                        this.filteredManuals = this.availableManuals
                        this.loadAreas()
                        this.loadTickets()
                    }
                },
                immediate: true
            },
            currentState() {
                this.$vuetify.goTo(0, { duration: 0 })
            }
        },
        methods: {
            loadAreas() {
                this.loadingAreas = true
                ticketsService.getAreas().then(response => {
                    if (response.success) {
                        this.areas = response.data
                    }
                }).finally(() => {
                    this.loadingAreas = false
                })
            },
            loadTickets() {
                this.loadingTickets = true
                ticketsService.getTicketsByEmail(this.getProfile.email, 100).then(response => {
                    if (response.success) {
                        this.tickets = response.data
                    }
                }).finally(() => {
                    this.loadingTickets = false
                })
            },
            filterManuals() {
                if (!this.searchQuery.trim()) {
                    this.filteredManuals = this.availableManuals
                    return
                }
                const query = this.searchQuery.toLowerCase()
                this.filteredManuals = this.availableManuals.filter(manual => {
                    const titleMatch = manual.title.toLowerCase().includes(query)
                    const descMatch = manual.description.toLowerCase().includes(query)
                    const keywords = manual.keywords || []
                    const keywordMatch = keywords.some(kw => kw.includes(query))
                    return titleMatch || descMatch || keywordMatch
                })
            },
            startReadingManual(manual) {
                this.readingManual = manual
                this.pdfSrc = null
                this.numPages = 0
                this.loadingPdf = false
                this.currentState = 'reading-manual'

                if (manual.link && manual.link !== '#') {
                    this.loadingPdf = true
                    setTimeout(() => {
                        const task = pdf.createLoadingTask(manual.link)
                        this.pdfSrc = task
                        task.promise.then(pdfObj => {
                            this.numPages = pdfObj.numPages
                            this.loadingPdf = false
                        }).catch(err => {
                            console.error('PDF error', err)
                            this.loadingPdf = false
                        })
                    }, 350)
                }
            },
            printPdf() {
                if (this.$refs.myPdfComponent) {
                    this.$refs.myPdfComponent.print()
                }
            },
            resolvedProblem() {
                this.successDialog = true
            },
            updateRelatedManuals() {
                const text = (this.ticketForm.title + ' ' + this.ticketForm.description).toLowerCase()
                if (text.length < 3) {
                    this.relatedManuals = []
                    return
                }

                this.relatedManuals = this.availableManuals.filter(manual => {
                    const keywords = manual.keywords || []
                    const hasMatch = keywords.some(kw => text.includes(kw))
                    return hasMatch
                }).slice(0, 5)
            },
            goToCreateTicket() {
                const profile = this.getProfile || {}
                this.ticketForm.client_name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
                this.ticketForm.client_email = profile.email || ''
                this.ticketForm.client_phone = profile.celular || ''
                this.errores = {}
                this.errorAlert = ''
                this.currentState = 'create-ticket'
            },
            goToMyTickets() {
                this.currentState = 'my-tickets'
                this.loadTickets()
            },
            async submitTicket() {
                this.errores = {}
                this.errorAlert = ''

                if (!this.ticketForm.title || !this.ticketForm.title.trim()) {
                    this.errores.title = ['El título es requerido']
                    return
                }

                this.submittingTicket = true

                try {
                    const payload = {
                        title: this.ticketForm.title,
                        description: this.ticketForm.description || '',
                        area_id: this.ticketForm.area_id || null,
                        client_name: this.ticketForm.client_name || '',
                        client_email: this.ticketForm.client_email || '',
                        client_phone: this.ticketForm.client_phone || ''
                    }

                    const response = await ticketsService.createTicket(payload)

                    this.lastTicketFolio = response.data.folio
                    this.ticketSubmittedDialog = true
                    this.loadTickets()
                } catch (error) {
                    console.error('Error submitting ticket:', error)
                    if (error.response && error.response.data && error.response.data.message) {
                        this.errorAlert = error.response.data.message
                    } else {
                        this.errorAlert = 'Error al crear el ticket. Por favor, intenta nuevamente.'
                    }
                } finally {
                    this.submittingTicket = false
                }
            },
            openTicketDetails(ticket) {
                this.loadingTickets = true
                ticketsService.getTicketById(ticket.id).then(response => {
                    if (response.success) {
                        this.selectedTicket = response.data
                        this.ticketDetailsDialog = true
                    }
                }).finally(() => {
                    this.loadingTickets = false
                })
            },
            getStatusColor(status) {
                const colorMap = {
                    'Nuevo': '#FF9800',
                    'En progreso': '#2196F3',
                    'Resuelto': '#4CAF50',
                    'Cerrado': '#9E9E9E'
                }
                return colorMap[status] || '#757575'
            },
            formatDate(dateString) {
                if (!dateString) return '—'
                const date = new Date(dateString)
                return date.toLocaleDateString('es-MX', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                })
            },
            resetTicketForm() {
                const profile = this.getProfile || {}
                this.ticketForm = {
                    title: '',
                    description: '',
                    area_id: null,
                    client_name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
                    client_email: profile.email || '',
                    client_phone: profile.celular || ''
                }
                this.relatedManuals = []
                this.errores = {}
                this.errorAlert = ''
                this.currentState = 'create-ticket'
            },
            async submitQuickTicket() {
                this.errores = {}
                this.errorAlert = ''

                if (!this.quickTicketForm.title || !this.quickTicketForm.title.trim()) {
                    this.errores.title = ['El título es requerido']
                    return
                }

                this.submittingTicket = true

                try {
                    const profile = this.getProfile || {}
                    const payload = {
                        title: this.quickTicketForm.title,
                        description: this.quickTicketForm.description || '',
                        area_id: this.quickTicketForm.area_id || null,
                        client_name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
                        client_email: profile.email || '',
                        client_phone: profile.celular || ''
                    }

                    const response = await ticketsService.createTicket(payload)

                    this.lastTicketFolio = response.data.folio
                    this.showTicketPanel = false
                    this.ticketSubmittedDialog = true
                    this.quickTicketForm = {
                        title: '',
                        description: '',
                        area_id: null
                    }
                    this.loadTickets()
                } catch (error) {
                    console.error('Error submitting ticket:', error)
                    if (error.response && error.response.data && error.response.data.message) {
                        this.errorAlert = error.response.data.message
                    } else {
                        this.errorAlert = 'Error al crear el ticket. Por favor, intenta nuevamente.'
                    }
                } finally {
                    this.submittingTicket = false
                }
            }
        }
    }
</script>

<style scoped>
.help-center-container {
  min-height: 100vh;
  padding: 24px 0;
}

/* Help Hub State */
.help-hub {
  max-width: 1200px;
  margin: 0 auto;
}

.help-header {
  text-align: center;
  background: linear-gradient(135deg, #002b48 0%, #004a6e 100%);
  color: white;
  padding: 48px 24px;
  border-radius: 24px;
  margin-bottom: 40px;
  box-shadow: 0 8px 16px rgba(0, 43, 72, 0.15);
}

.help-header h1 {
  margin-bottom: 8px;
  color: white !important;
  font-size: 2.5rem;
  letter-spacing: -0.5px;
}

.help-header p {
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.05rem;
  letter-spacing: 0.3px;
}

/* Tab Content */
.tab-content {
  background: transparent !important;
  padding: 20px !important;
}

.manual-list-container {
  background: transparent !important;
  padding: 0 !important;
}

.manual-list-item {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  margin-bottom: 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  border-left: 4px solid transparent;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.08);
  overflow: hidden;
  padding: 12px !important;
}

.manual-list-item:hover {
  background: linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 100%);
  border-left-color: #2196F3;
  transform: translateX(4px);
  box-shadow: 0 4px 16px rgba(33, 150, 243, 0.15);
}

.related-manuals-title {
  color: #2196F3;
}

/* Tabs Styling */
.help-tabs {
  background: white !important;
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.help-tabs ::v-deep .v-tabs__bar {
  border-radius: 12px;
  background: transparent !important;
}

.help-tabs ::v-deep .v-tab {
  color: #666 !important;
  font-size: 0.95rem !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  min-width: 150px;
  transition: all 0.3s ease;
}

.help-tabs ::v-deep .v-tab:hover {
  color: #2196F3 !important;
}

.help-tabs ::v-deep .v-tab--active {
  color: #2196F3 !important;
}

.tab-label {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px;
}

/* FAQ Section */
.faq-list-container ::v-deep .v-list__group__header {
  border-radius: 12px;
}

.faq-list-item {
  padding: 0 !important;
}

.faq-list-item ::v-deep .v-list__group__header .v-list__tile {
  border-radius: 12px;
}

.faq-list-item ::v-deep .v-list__group__header__append-icon .v-icon {
  color: #2196F3 !important;
}

.faq-list-item ::v-deep .v-list__group__items .v-list__tile {
  height: auto !important;
  min-height: 0;
  white-space: normal;
  padding: 0 16px 20px 72px;
  align-items: flex-start;
}

.faq-list-item ::v-deep .v-list__group__items .v-list__tile__content {
  align-items: flex-start;
}

.faq-answer-text {
  white-space: normal;
  color: #555;
  line-height: 1.7;
  font-size: 0.95rem;
}

.faq-tips {
  width: 100%;
  background: linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 100%);
  padding: 12px;
  border-radius: 8px;
  border-left: 4px solid #2196F3;
}

.faq-tips-label {
  display: inline-flex;
  align-items: center;
  color: #1976d2;
}

.faq-tips .caption {
  white-space: normal;
  color: #555;
  line-height: 1.5;
}

/* Reading Manual State */
.reading-state {
  max-width: 1000px;
  margin: 0 auto;
}

.pdf-viewer-container {
  min-height: 600px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 12px;
  padding: 40px 20px;
  margin: 20px 0;
}

.loading-state,
.not-available-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex: 1;
}

.pdf-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.resolution-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

/* Create Ticket State */
.create-ticket-state {
  max-width: 1200px;
  margin: 0 auto;
}

.related-manual-item {
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 6px;
  margin-bottom: 4px;
}

.related-manual-item:hover {
  background: rgba(33, 150, 243, 0.1);
}

/* My Tickets State */
.my-tickets-state {
  max-width: 1000px;
  margin: 0 auto;
}

.ticket-list-item {
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 12px;
  border-radius: 8px;
  background: #f9f9f9;
  border-left: 4px solid transparent;
}

.ticket-list-item:hover {
  background: #eeeeee;
  border-left-color: #2196F3;
  transform: translateX(4px);
}

/* Tab Items */
.help-tabs ::v-deep .v-tabs__items {
  background: transparent !important;
  padding-top: 20px;
}

.help-tabs ::v-deep .v-window__container {
  background: transparent !important;
}

.help-tabs ::v-deep .v-window-item {
  background: transparent !important;
}

/* PDF page wrapper */
.pdf-page-wrapper {
  width: 100%;
  max-width: 860px;
  margin-bottom: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
  background: white;
  overflow: visible;
}

.pdf-page-wrapper canvas {
  width: 100% !important;
  height: auto !important;
  display: block;
}

/* Floating Help Bubble */
.floating-help-bubble {
  position: fixed;
  bottom: 32px;
  right: 32px;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(33, 150, 243, 0.4);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 50;
  border: 4px solid white;
}

.floating-help-bubble:hover {
  transform: scale(1.12);
  box-shadow: 0 8px 24px rgba(33, 150, 243, 0.6);
}

.floating-help-bubble:active {
  transform: scale(0.95);
}

/* Ticket Panel Overlay */
.ticket-panel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 100;
  display: flex;
  align-items: flex-end;
}

/* Ticket Panel */
.ticket-panel {
  width: 100%;
  max-width: 100%;
  border-radius: 28px 28px 0 0 !important;
  max-height: 80vh;
  overflow-y: auto;
  animation: slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
}

.ticket-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px 24px;
  border-bottom: 1px solid #e8e8e8;
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  border-radius: 28px 28px 0 0;
  position: sticky;
  top: 0;
  z-index: 101;
}

.ticket-panel-header h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #1565c0;
  font-weight: 700;
}

.ticket-panel-content {
  padding: 28px 24px;
  background: white;
}

.ticket-panel-content ::v-deep .v-text-field,
.ticket-panel-content ::v-deep .v-textarea,
.ticket-panel-content ::v-deep .v-select {
  margin-bottom: 20px;
}

.ticket-panel-content ::v-deep .v-text-field--rounded {
  border-radius: 12px;
}

.ticket-panel-content ::v-deep .v-text-field fieldset,
.ticket-panel-content ::v-deep .v-textarea fieldset {
  border-radius: 12px !important;
}

/* Slide Up Animation */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.slide-up-enter {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.ticket-panel-overlay.slide-up-enter-active,
.ticket-panel-overlay.slide-up-leave-active {
  transition: background-color 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.slide-up-enter .ticket-panel-overlay,
.slide-up-leave-to .ticket-panel-overlay {
  background-color: transparent;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .help-header {
    padding: 30px 16px;
  }

  .help-header h1 {
    font-size: 1.75rem !important;
  }

  .resolution-buttons {
    flex-direction: column;
  }

  .resolution-buttons button {
    width: 100% !important;
  }

  .floating-help-bubble {
    bottom: 20px;
    right: 20px;
    width: 56px;
    height: 56px;
  }

  .ticket-panel {
    max-height: 90vh;
  }
}

</style>
